import React from 'react'
import styled from 'styled-components'
import { Typography, Flex } from 'components'

export type SliderProps = {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  'data-testid'?: string
}

const SliderContainer = styled.div`
  width: 100%;
`

const SliderInput = styled.input<{ $value: number; $min: number; $max: number }>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(to right, #ef4444 0%, #eab308 50%, #22c55e 100%);
  outline: none;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ $value, $min, $max }) => {
      const percentage = (($value - $min) / ($max - $min)) * 100
      if (percentage <= 50) {
        // Red to Yellow (0-50%)
        const factor = percentage / 50
        return `rgb(${239 - (239 - 234) * factor}, ${68 + (179 - 68) * factor}, ${68 - 68 * factor})`
      } else {
        // Yellow to Green (50-100%)
        const factor = (percentage - 50) / 50
        return `rgb(${234 - (234 - 34) * factor}, ${179 + (197 - 179) * factor}, ${8 + (94 - 8) * factor})`
      }
    }};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.white};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ $value, $min, $max }) => {
      const percentage = (($value - $min) / ($max - $min)) * 100
      if (percentage <= 50) {
        // Red to Yellow (0-50%)
        const factor = percentage / 50
        return `rgb(${239 - (239 - 234) * factor}, ${68 + (179 - 68) * factor}, ${68 - 68 * factor})`
      } else {
        // Yellow to Green (50-100%)
        const factor = (percentage - 50) / 50
        return `rgb(${234 - (234 - 34) * factor}, ${179 + (197 - 179) * factor}, ${8 + (94 - 8) * factor})`
      }
    }};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.white};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.grey200};

    &::-webkit-slider-thumb {
      cursor: not-allowed;
      background: ${({ theme }) => theme.colors.grey400};
    }

    &::-moz-range-thumb {
      cursor: not-allowed;
      background: ${({ theme }) => theme.colors.grey400};
    }
  }
`

const SliderValue = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`

export const Slider: React.FC<SliderProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  'data-testid': dataTestId,
  ...props
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value))
  }

  // Calculate color based on value
  const getValueColor = () => {
    if (disabled) return 'grey400'

    const percentage = ((value - min) / (max - min)) * 100
    if (percentage <= 25) return 'red500'
    if (percentage <= 50) return 'orange500'
    if (percentage <= 75) return 'yellow500'
    return 'green500'
  }

  return (
    <SliderContainer>
      <Flex between="zero" middle="zero" style={{ marginBottom: '8px' }}>
        <Typography fontSize="text_sm" fontWeight="medium" color="grey700">
          {label}
        </Typography>
        <Typography fontSize="text_sm" fontWeight="medium" color={getValueColor()}>
          {value}
        </Typography>
      </Flex>
      <SliderInput
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        data-testid={dataTestId}
        $value={value}
        $min={min}
        $max={max}
        {...props}
      />
      <SliderValue>
        <Typography fontSize="text_xs" color="grey400">
          {min}
        </Typography>
        <Typography fontSize="text_xs" color="grey400">
          {max}
        </Typography>
      </SliderValue>
    </SliderContainer>
  )
}
